import type { LoginSuccessResponse } from '@/api/auth/auth.types'
import { getUserProfile } from '@/api/workspace/profile.actions'
import { toastError } from '@/lib/toast'
import { saveAccessToken } from '@/lib/token-storage'
import { API_BASE_URL } from '@/config/api'
import {
  getSuporteAppBaseUrl,
  getSuporteAppHandoffHref,
  getAdmindeskAppBaseUrl,
  getAdmindeskAppHandoffHref,
  redirectMainAppHandoff,
} from '@/lib/post-login-redirect'
import {
  buildCompletarCelularHref,
  loginResponseRequiresPhoneVerification,
  userRequiresPhoneVerification,
} from '@/lib/phone-verification-gate'

export type CompleteLoginContext = {
  returnApp?: 'suporte' | 'admindesk'
  next?: string
}

export type LoginTokensInput =
  | LoginSuccessResponse
  | Pick<LoginSuccessResponse, 'accessToken'>

async function bootstrapRefreshCookie(accessToken: string): Promise<void> {
  const base = API_BASE_URL ? API_BASE_URL.replace(/\/$/, '') : ''
  try {
    await fetch(`${base}/api/v2/auth/bootstrap-refresh-cookie`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
    })
  } catch {
    // segue com access token em memória
  }
}

async function resolveNeedsPhoneVerification(
  tokens: LoginTokensInput,
): Promise<boolean> {
  if (
    'requiresPhoneVerification' in tokens &&
    loginResponseRequiresPhoneVerification(tokens)
  ) {
    return true
  }

  try {
    const profile = await getUserProfile()
    return userRequiresPhoneVerification(
      profile.data.celular,
      profile.data.celularConfirmado,
    )
  } catch {
    return false
  }
}

/**
 * Conclui login após obter access/refresh (senha, magic link, Google, etc.).
 */
export async function completeLoginWithTokens(
  tokens: LoginTokensInput,
  ctx: CompleteLoginContext,
): Promise<boolean> {
  saveAccessToken(tokens.accessToken)
  await bootstrapRefreshCookie(tokens.accessToken)

  if (await resolveNeedsPhoneVerification(tokens)) {
    window.location.assign(
      buildCompletarCelularHref({
        next: ctx.next,
        return_app: ctx.returnApp,
      }),
    )
    return true
  }

  if (ctx.returnApp === 'suporte') {
    if (!getSuporteAppBaseUrl()) {
      toastError(
        'Configure VITE_SUPORTE_APP_URL no app de login para concluir o acesso à central de ajuda.',
      )
      return false
    }
    try {
      window.location.href = getSuporteAppHandoffHref(tokens, {
        next: ctx.next,
      })
      return true
    } catch (e) {
      toastError(
        (e as Error).message ||
          'Não foi possível concluir o login na central de ajuda.',
      )
      return false
    }
  }

  if (ctx.returnApp === 'admindesk') {
    if (!getAdmindeskAppBaseUrl()) {
      toastError(
        'Configure VITE_ADMINDESK_APP_URL no app de login para concluir o retorno ao AdminDesk.',
      )
      return false
    }
    try {
      window.location.href = getAdmindeskAppHandoffHref(tokens, {
        next: ctx.next,
      })
      return true
    } catch (e) {
      toastError(
        (e as Error).message ||
          'Não foi possível retornar ao AdminDesk.',
      )
      return false
    }
  }

  try {
    redirectMainAppHandoff(tokens, { next: ctx.next })
    return true
  } catch (e) {
    toastError(
      (e as Error).message ||
        'Configure VITE_MAIN_APP_URL para concluir o login.',
    )
    return false
  }
}
